import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus } from "lucide-react";
import { useDefaultFunctions } from "@/hooks/useDefaultFunctions";
import { cn } from "@/lib/utils";
import type {
  LocationType,
  ProjectFunctionGroup,
} from "@/hooks/useProjectPeople";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AddFunctionDialogProps {
  planningDays: Date[];
  eventDays: Date[];
  groups: ProjectFunctionGroup[];
  onAdd: (data: {
    name: string;
    project_function_group_id?: string | null;
    required_count: number;
    selected_days: string[];
    system_tag?: string | null;
    location_type?: LocationType;
  }) => void;
  isLoading?: boolean;
  triggerClassName?: string;
}

function toDateString(day: Date) {
  return format(day, "yyyy-MM-dd");
}

export function AddFunctionDialog({
  planningDays,
  eventDays,
  groups,
  onAdd,
  isLoading,
  triggerClassName,
}: AddFunctionDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [requiredCount, setRequiredCount] = useState(1);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [isCameraPosition, setIsCameraPosition] = useState(false);
  const [locationType, setLocationType] = useState<LocationType>(null);
  const [groupId, setGroupId] = useState<string | null>(null);

  const { data: defaultFunctions } = useDefaultFunctions();

  const planningDayOptions = useMemo(
    () =>
      planningDays.map((day) => ({
        value: toDateString(day),
        label: format(day, "EEEE, dd.MM.yyyy"),
        shortLabel: format(day, "EEE dd.MM"),
      })),
    [planningDays],
  );

  const eventDayValues = useMemo(
    () => eventDays.map((day) => toDateString(day)),
    [eventDays],
  );

  const toggleDay = (day: string) => {
    setSelectedDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort(),
    );
  };

  const handleSelectAllPlanningDays = () => {
    setSelectedDays(planningDayOptions.map((day) => day.value));
  };

  const handleSelectEventDays = () => {
    setSelectedDays(eventDayValues);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selectedDays.length === 0) return;

    onAdd({
      name: name.trim(),
      project_function_group_id: groupId,
      required_count: requiredCount,
      selected_days: selectedDays,
      system_tag: isCameraPosition ? "camera_position" : null,
      location_type: locationType,
    });

    setOpen(false);
    setName("");
    setRequiredCount(1);
    setSelectedDays([]);
    setIsCameraPosition(false);
    setLocationType(null);
    setGroupId(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className={cn(triggerClassName)}
          data-testid="add-function-trigger"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Function
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="add-function-dialog">
        <DialogHeader>
          <DialogTitle>Add Project Function</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {defaultFunctions && defaultFunctions.length > 0 && (
            <div className="space-y-2">
              <Label>Quick Select</Label>
              <div className="flex flex-wrap gap-1.5">
                {defaultFunctions.map((fn) => (
                  <Badge
                    key={fn.id}
                    variant={name === fn.name ? "default" : "outline"}
                    className="cursor-pointer hover:bg-primary/20 transition-colors"
                    onClick={() => {
                      setName(fn.name);
                      setGroupId(fn.default_function_group_id ?? null);
                      setIsCameraPosition(fn.system_tag === "camera_position");
                      setLocationType(fn.location_type ?? null);
                    }}
                  >
                    {fn.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="function-name">Function Name</Label>
            <Input
              id="function-name"
              data-testid="function-name-input"
              placeholder="e.g., Camera Operator"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="camera-position"
              data-testid="camera-position-checkbox"
              checked={isCameraPosition}
              onCheckedChange={(checked) => setIsCameraPosition(!!checked)}
            />
            <Label htmlFor="camera-position" className="cursor-pointer">
              Camera position
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="function-group">Group</Label>
            <Select
              value={groupId ?? "none"}
              onValueChange={(v) => setGroupId(v === "none" ? null : v)}
            >
              <SelectTrigger id="function-group">
                <SelectValue placeholder="No group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No group</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="function-location-type">Location</Label>
            <Select
              value={locationType ?? "none"}
              onValueChange={(v) =>
                setLocationType(
                  v === "none" ? null : (v as "remote" | "on_site"),
                )
              }
            >
              <SelectTrigger id="function-location-type">
                <SelectValue placeholder="Not specified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use group location</SelectItem>
                <SelectItem value="on_site">On-site</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="function-required-count">Required People</Label>
            <Input
              id="function-required-count"
              data-testid="function-required-count-input"
              type="number"
              min={1}
              value={requiredCount}
              onChange={(e) => setRequiredCount(parseInt(e.target.value) || 1)}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label>Select Days</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="select-all-planning-days"
                  onClick={handleSelectAllPlanningDays}
                >
                  All planning days
                </Button>
                {eventDayValues.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="select-event-days"
                    onClick={handleSelectEventDays}
                  >
                    Event dates
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-md border">
              <ScrollArea className="h-56 p-3">
                <div className="space-y-2">
                  {planningDayOptions.map((day) => (
                    <label
                      key={day.value}
                      data-testid={`function-day-option-${day.value}`}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={selectedDays.includes(day.value)}
                        onCheckedChange={() => toggleDay(day.value)}
                      />
                      <span className="text-sm">{day.label}</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {selectedDays.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {planningDayOptions
                  .filter((day) => selectedDays.includes(day.value))
                  .map((day) => (
                    <Badge key={day.value} variant="secondary">
                      {day.shortLabel}
                    </Badge>
                  ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="cancel-function-button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-function-button"
              disabled={!name.trim() || selectedDays.length === 0 || isLoading}
            >
              Add Function
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
